import { useState } from 'react';
import { Clock, Users, MapPin, Pencil, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { DifficultyBadge } from '@/components/DifficultyBadge';

interface PracticalInfo {
  debut: string | null;
  duree: string | null;
  participants_min: number;
  participants_max: number;
  niveau: number;
}

interface ExcursionPracticalInfoEditorProps {
  excursionId: string;
  info: PracticalInfo;
  isAdmin: boolean;
  onUpdate: (updates: Partial<PracticalInfo>) => void;
}

export const ExcursionPracticalInfoEditor = ({
  excursionId,
  info,
  isAdmin,
  onUpdate
}: ExcursionPracticalInfoEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedInfo, setEditedInfo] = useState<PracticalInfo>(info);
  const { toast } = useToast();

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('excursions')
        .update({
          debut: editedInfo.debut,
          duree: editedInfo.duree,
          participants_min: editedInfo.participants_min,
          participants_max: editedInfo.participants_max,
          niveau: editedInfo.niveau
        })
        .eq('id', excursionId);

      if (error) throw error;

      onUpdate(editedInfo);
      setIsEditing(false);
      toast({
        title: "Infos mises à jour",
        description: "Les informations pratiques ont été enregistrées"
      });
    } catch (error) {
      console.error('Error updating practical info:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les modifications",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedInfo(info);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="bg-card rounded-lg p-6 border shadow-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-poppins font-bold">Infos pratiques</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={handleCancel} disabled={saving}>
              <X className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="debut">Heure de début</Label>
            <Input
              id="debut"
              value={editedInfo.debut || ''}
              onChange={(e) => setEditedInfo(prev => ({ ...prev, debut: e.target.value }))}
              placeholder="Ex: 8h00"
            />
          </div>
          
          <div>
            <Label htmlFor="duree">Durée</Label>
            <Input
              id="duree"
              value={editedInfo.duree || ''}
              onChange={(e) => setEditedInfo(prev => ({ ...prev, duree: e.target.value }))}
              placeholder="Ex: 6 heures"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="participants_min">Participants min</Label>
              <Input
                id="participants_min"
                type="number"
                min={1}
                value={editedInfo.participants_min}
                onChange={(e) => setEditedInfo(prev => ({ ...prev, participants_min: parseInt(e.target.value) || 1 }))}
              />
            </div>
            <div>
              <Label htmlFor="participants_max">Participants max</Label>
              <Input
                id="participants_max"
                type="number"
                min={1}
                value={editedInfo.participants_max}
                onChange={(e) => setEditedInfo(prev => ({ ...prev, participants_max: parseInt(e.target.value) || 10 }))}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="niveau">Niveau de difficulté</Label>
            <Select
              value={String(editedInfo.niveau)}
              onValueChange={(value) => setEditedInfo(prev => ({ ...prev, niveau: parseInt(value) }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 - Très facile</SelectItem>
                <SelectItem value="2">2 - Facile</SelectItem>
                <SelectItem value="3">3 - Modéré</SelectItem>
                <SelectItem value="4">4 - Difficile</SelectItem>
                <SelectItem value="5">5 - Très difficile</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-6 border shadow-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-poppins font-bold">Infos pratiques</h3>
        {isAdmin && (
          <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm text-muted-foreground">Début</div>
            <div className="font-semibold">{info.debut || 'Non spécifié'}</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm text-muted-foreground">Durée</div>
            <div className="font-semibold">{info.duree || 'Non spécifiée'}</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Users className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm text-muted-foreground">Participants</div>
            <div className="font-semibold">
              {info.participants_min} à {info.participants_max} personnes
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm text-muted-foreground mb-2">Niveau de difficulté</div>
            <DifficultyBadge niveau={info.niveau} size="md" />
          </div>
        </div>
      </div>
    </div>
  );
};
